import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Star, ShieldCheck, CalendarDays, Sparkles, ChevronRight, ChevronDown, MapPin, Lock, Zap } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import TrainerCard from "@/components/TrainerCard";
import SEOHead from "@/components/SEOHead";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1548690312-e3b507d8c110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxwZXJzb25hbCUyMHRyYWluZXIlMjBneW0lMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODMwMDQ0OTF8MA&ixlib=rb-4.1.0&q=85";
const BECOME = "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxoaWdoJTIwZW5kJTIwZml0bmVzcyUyMGd5bXxlbnwwfHx8fDE3ODMwMDQ0OTJ8MA&ixlib=rb-4.1.0&q=85";

const GOALS = [
  { name: "Weight Loss", img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&q=70" },
  { name: "Muscle Gain", img: "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?auto=format&fit=crop&w=800&q=70" },
  { name: "Strength Training", img: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=70" },
  { name: "Sports Performance", img: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=800&q=70" },
  { name: "Online Coaching", img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=800&q=70" },
  { name: "Rehabilitation", img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=70" },
  { name: "Nutrition", img: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=800&q=70" },
];

const TESTIMONIALS = [
  { name: "Sarah D., Dublin", text: "I found Aoife in 2 minutes and lost 8kg in 12 weeks. The booking flow just works.", rating: 5, avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=facearea&facepad=2&w=200&h=200&q=70" },
  { name: "James M., Cork", text: "Cian is the real deal — proper strength coach. Loved seeing certificates before booking.", rating: 5, avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=facearea&facepad=2&w=200&h=200&q=70" },
  { name: "Rachel B., Galway", text: "Niamh's yoga therapy fixed a back issue I've had for two years. Highly recommend.", rating: 5, avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=facearea&facepad=2&w=200&h=200&q=70" },
];

const STEPS = [
  { n: "01", t: "Search & Compare", d: "Filter trainers by county, gym, price, speciality and goal — Ireland-wide.", img: "https://images.unsplash.com/photo-1522199755839-a2bacb67c546?auto=format&fit=crop&w=800&q=70" },
  { n: "02", t: "Book Instantly", d: "Pick a time from your trainer's live calendar and pay securely online.", img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=70" },
  { n: "03", t: "Train & Transform", d: "Attend your session, track progress, leave a review — repeat until goal hit.", img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=800&q=70" },
];

const FAQS = [
  { q: "Are all trainers verified?", a: "Yes. Every trainer is manually reviewed by our admin team before going live. We check qualifications, insurance and experience." },
  { q: "How do payments work?", a: "You pay securely at booking. Funds are released to the trainer after your session, so you're always protected." },
  { q: "Can I train online instead of in-person?", a: "Many of our trainers offer online coaching programmes. Filter by 'Online Coaching' to find them." },
  { q: "What if I need to cancel?", a: "You can cancel any pending booking from your dashboard. Cancellation policies vary by trainer." },
  { q: "How do I become a trainer on FitLink?", a: "Sign up as a trainer, submit your qualifications, and our team will review your profile within 48 hours." },
];

export default function HomePage() {
  const [q, setQ] = useState("");
  const [county, setCounty] = useState("");
  const [goal, setGoal] = useState("");
  const nav = useNavigate();

  const { data: featured = [] } = useQuery({
    queryKey: ["featured-trainers"],
    queryFn: async () => (await api.get("/trainers/featured")).data,
  });
  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: async () => (await api.get("/meta/goals")).data,
  });
  const { data: stats } = useQuery({
    queryKey: ["site-stats"],
    queryFn: async () => (await api.get("/meta/stats")).data,
  });

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (county) params.set("county", county);
    if (goal) params.set("goal", goal);
    nav(`/trainers?${params.toString()}`);
  };

  return (
    <div>
      <SEOHead />
      {/* HERO */}
      <section data-testid="home-hero" className="relative h-[640px] overflow-hidden">
        <img src={HERO} alt="Personal trainer" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 hero-overlay"/>
        <div className="relative container-fl h-full flex flex-col justify-center">
          <div className="max-w-3xl fade-up">
            <div className="overline text-emerald-400 mb-4">FitLink Ireland — Premium Personal Trainer Marketplace</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
              Find your perfect<br/><span className="text-emerald-400">personal trainer.</span>
            </h1>
            <p className="mt-6 text-lg text-neutral-200 max-w-xl leading-relaxed">
              Verified coaches across Ireland. Book securely, train smarter, achieve real results.
            </p>
          </div>

          <form onSubmit={submitSearch} data-testid="hero-search-form" className="mt-10 bg-white rounded-2xl shadow-2xl border border-neutral-200 p-2 max-w-3xl flex flex-col sm:flex-row gap-2 fade-up-delay-1">
            <div className="flex-1 flex items-center gap-2 px-4 py-2">
              <Search size={16} className="text-neutral-400"/>
              <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Trainer name, speciality…"
                     className="w-full bg-transparent outline-none text-sm placeholder:text-neutral-400"/>
            </div>
            <div className="relative flex-1 flex items-center gap-2 px-4 py-2 border-t sm:border-t-0 sm:border-l border-neutral-200">
              <MapPin size={16} className="text-neutral-400"/>
              <select data-testid="hero-search-county" value={county} onChange={(e) => setCounty(e.target.value)}
                      className="w-full bg-transparent outline-none text-sm appearance-none">
                <option value="">Any county</option>
                {(meta?.counties || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={14} className="text-neutral-400 pointer-events-none"/>
            </div>
            <div className="relative flex-1 flex items-center gap-2 px-4 py-2 border-t sm:border-t-0 sm:border-l border-neutral-200">
              <Sparkles size={16} className="text-neutral-400"/>
              <select data-testid="hero-search-goal" value={goal} onChange={(e) => setGoal(e.target.value)}
                      className="w-full bg-transparent outline-none text-sm appearance-none">
                <option value="">Any goal</option>
                {(meta?.goals || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <ChevronDown size={14} className="text-neutral-400 pointer-events-none"/>
            </div>
            <button data-testid="hero-search-submit" type="submit" className="btn-accent whitespace-nowrap">Search <ChevronRight size={14}/></button>
          </form>

          <div className="mt-6 flex items-center gap-6 text-sm text-neutral-200 fade-up-delay-2">
            <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-400"/> Verified trainers</div>
            <div className="flex items-center gap-1.5"><CalendarDays size={14} className="text-emerald-400"/> Instant booking</div>
            <div className="flex items-center gap-1.5"><Star size={14} className="text-emerald-400"/> 4.9 average rating</div>
          </div>
        </div>
      </section>

      {/* FEATURED — only show when we have trainers */}
      {featured.length > 0 && (
      <section className="container-fl py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-2">Featured trainers</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Top-rated coaches in Ireland</h2>
          </div>
          <Link to="/trainers" data-testid="see-all-trainers" className="hidden md:inline-flex btn-secondary">See all <ChevronRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((t) => <TrainerCard key={t.id} t={t}/>)}
        </div>
      </section>
      )}

      {/* STATS / PROMISE STRIP */}
      <section className="bg-neutral-950 text-white py-14 border-y border-neutral-800">
        <div className="container-fl" data-testid="home-stats">
          {(stats?.trainers ?? 0) >= 5 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { v: `${stats?.trainers ?? "—"}+`, l: "Verified trainers" },
                { v: `${stats?.counties ?? "—"}`, l: "Counties covered" },
                { v: `${stats?.sessions ?? "—"}+`, l: "Sessions booked" },
                { v: `${stats?.avg_rating ?? "4.9"}`, l: `Avg rating · ${stats?.review_count ?? 0} reviews` },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-4xl sm:text-5xl font-black text-emerald-400">{s.v}</div>
                  <div className="text-xs sm:text-sm text-neutral-400 mt-2 uppercase tracking-widest font-semibold">{s.l}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { Icon: ShieldCheck, t: "Verified only", d: "Every trainer manually reviewed before going live." },
                { Icon: MapPin, t: "Ireland-wide", d: "Coaches across all 26 counties, online or in-person." },
                { Icon: Lock, t: "Secure payments", d: "Stripe-powered checkout. Your card details never touch our servers." },
                { Icon: Zap, t: "Instant booking", d: "Pick a slot, pay, get a confirmation email — in under a minute." },
              ].map(({ Icon, t, d }, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center ring-1 ring-emerald-500/30">
                    <Icon size={18} className="text-emerald-400" />
                  </div>
                  <div>
                    <div className="font-bold text-sm">{t}</div>
                    <div className="text-xs text-neutral-400 leading-relaxed mt-0.5">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GOALS */}
      <section className="bg-white border-y border-neutral-200 py-20">
        <div className="container-fl">
          <div className="overline mb-2">Popular fitness goals</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-10">What do you want to achieve?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {GOALS.map((g) => (
              <Link key={g.name} to={`/trainers?goal=${encodeURIComponent(g.name)}`}
                    data-testid={`goal-${g.name.replace(/\s+/g,'-').toLowerCase()}`}
                    className="group relative aspect-square rounded-xl overflow-hidden text-white flex items-end p-4 font-bold text-sm hover:scale-[1.03] transition-transform">
                <img src={g.img} alt={g.name}
                     className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10 group-hover:from-black/95"/>
                <span className="relative z-10 leading-tight drop-shadow-lg">{g.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-fl py-20">
        <div className="overline mb-2">How it works</div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-12">Three steps to your best self</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="card-fl overflow-hidden group">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={s.img} alt={s.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
                <div className="absolute top-4 left-4 text-5xl font-black text-white drop-shadow-lg">{s.n}</div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{s.t}</h3>
                <p className="text-neutral-600">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS — hidden until we have real reviews */}
      {(stats?.review_count ?? 0) >= 3 && (
      <section className="bg-neutral-950 text-white py-20">
        <div className="container-fl">
          <div className="overline mb-2 text-emerald-400">Client stories</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-12">Real results from real people</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-8 rounded-xl border border-neutral-800 bg-neutral-900">
                <div className="flex mb-4">
                  {Array.from({length: t.rating}).map((_, k) => <Star key={k} size={16} className="star-on fill-emerald-500"/>)}
                </div>
                <p className="text-neutral-200 leading-relaxed">"{t.text}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover ring-2 ring-emerald-500/40"/>
                  <div className="text-sm font-semibold">{t.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* BECOME A TRAINER */}
      <section className="relative py-24 overflow-hidden">
        <img src={BECOME} alt="Gym" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/40"/>
        <div className="relative container-fl">
          <div className="max-w-2xl text-white">
            <div className="overline text-emerald-400 mb-3">For personal trainers</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4">Grow your PT business with FitLink.</h2>
            <p className="text-neutral-200 mb-8 text-lg">Get discovered by thousands of clients across Ireland. Manage bookings, take payments and build reviews — all in one dashboard.</p>
            <Link to="/become-a-trainer" data-testid="cta-become-trainer" className="btn-accent">Join as a trainer <ChevronRight size={14}/></Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-fl py-20">
        <div className="overline mb-2">Questions</div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-8">Frequently asked</h2>
        <Accordion type="single" collapsible className="max-w-3xl">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-neutral-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
